// Versão: 1.02
// Data..: 2026-10-01
// v1.02: botão Link de pagamento por cobrança (copia/gera o link público /portal/pagar/<token>).
// v1.01: baixa manual avisa que NÃO cancela o boleto no Banco Inter (conciliação manual —
//        finding #5); aviso não-bloqueante de vencimento no passado no modal de nova
//        cobrança (finding #9, o aviso é só da UI; o backend aceita a data).
// Painel de Faturamento (Invoice) — SOMENTE ADMIN. Reutilizado como aba em Administração >
// Sistema. Lista as cobranças (mensais e avulsas) com filtros e cards de resumo, permite
// criar cobrança pontual, emitir boleto no Banco Inter, enviar e-mail, visualizar o PDF do
// boleto, dar baixa e cancelar. Mostra alerta de cadastro de pagador incompleto (⚠) ao lado
// do nome da empresa (completar o cadastro antes de emitir).

import { useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  InvoiceStatus,
  InvoiceStatusLabels,
  type Company,
  type Invoice,
  type Paginated,
  type BillingListResult,
  type BillingSummary,
  type PayerPendingCompany,
} from '@makweb/shared';
import { api } from '../lib/api.js';
import { config } from '../config.js';
import { getToken } from '../lib/auth.js';
import { useToast } from '../context/ToastContext.js';
import { useDialog } from '../context/DialogContext.js';
import { Modal } from '../components/Modal.js';

// Data de hoje em AAAA-MM-DD (para o aviso não-bloqueante de vencimento no passado).
const todayIso = () => new Date().toISOString().slice(0, 10);
// Formata moeda BRL.
const brl = (n?: number) => (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
// Formata AAAA-MM-DD como dd/mm/aaaa (sem fuso).
const dateBr = (iso?: string) => {
  if (!iso) return '-';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

// Classe de badge por situação (reusa as cores das badges existentes).
const statusBadge: Record<string, string> = {
  draft: 'badge-normal',
  issued: 'badge-open',
  paid: 'badge-closed',
  overdue: 'badge-high',
  canceled: 'badge-closed',
};

// Cartão de KPI (mesmo visual do FinanceKpi).
function Kpi({ icon, label, value, tone }: { icon: string; label: string; value: string; tone?: 'ok' | 'warn' | 'bad' }) {
  const color = tone === 'bad' ? 'var(--danger)' : tone === 'warn' ? 'var(--warning)' : tone === 'ok' ? 'var(--success)' : undefined;
  return (
    <div className="stat-card">
      <span className="stat-icon">{icon}</span>
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={color ? { color } : undefined}>{value}</div>
    </div>
  );
}

const emptyForm = {
  companyId: '',
  description: '',
  amount: '',
  dueDate: '',
  clientEmail: '',
  issueNow: true,
  sendEmail: true,
};

export function InvoicingPanel() {
  const toast = useToast();
  const { confirm } = useDialog();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [pending, setPending] = useState<PayerPendingCompany[]>([]);
  const [items, setItems] = useState<Invoice[]>([]);
  const [summary, setSummary] = useState<BillingSummary | null>(null);
  const [loading, setLoading] = useState(false);

  // Filtros.
  const [fCompany, setFCompany] = useState('');
  const [fStatus, setFStatus] = useState<string>('all');
  const [fFrom, setFFrom] = useState('');
  const [fTo, setFTo] = useState('');
  const [fSearch, setFSearch] = useState('');

  // Modal de nova cobrança pontual.
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  // Modal do link de pagamento público (copia/mostra o link /portal/pagar/<token>).
  const [linkModal, setLinkModal] = useState<{ open: boolean; url: string; number?: number }>({ open: false, url: '' });

  const companyName = (id: string) => companies.find((c) => c.id === id)?.name ?? id;
  // Mapa companyId → campos faltantes (para o alerta ⚠ ao lado do nome).
  const pendingByCompany = useMemo(() => {
    const m = new Map<string, string[]>();
    for (const p of pending) m.set(p.companyId, p.missing);
    return m;
  }, [pending]);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (fCompany) params.set('company', fCompany);
    if (fStatus && fStatus !== 'all') params.set('status', fStatus);
    if (fFrom) params.set('from', fFrom);
    if (fTo) params.set('to', fTo);
    if (fSearch.trim()) params.set('search', fSearch.trim());
    api.get<BillingListResult>(`/invoices?${params.toString()}`)
      .then((r) => { setItems(r.items); setSummary(r.summary); })
      .catch((e) => toast.error(e instanceof Error ? e.message : 'Falha ao carregar as cobranças'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    api.get<Paginated<Company>>('/companies').then((r) => setCompanies(r.items)).catch(() => {});
    api.get<PayerPendingCompany[]>('/invoices/payer-pending').then(setPending).catch(() => {});
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Pré-preenche e-mail ao escolher a empresa no modal.
  const onPickCompany = (companyId: string) => {
    const c = companies.find((x) => x.id === companyId);
    setForm((f) => ({ ...f, companyId, clientEmail: c?.email ?? f.clientEmail }));
  };

  const openNew = () => { setForm({ ...emptyForm }); setOpen(true); };
  const closeModal = () => { setOpen(false); setForm({ ...emptyForm }); };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.companyId) { toast.error('Selecione a empresa.'); return; }
    const amount = Number(form.amount.replace(',', '.'));
    if (!Number.isFinite(amount) || amount <= 0) { toast.error('Informe um valor válido.'); return; }
    if (!form.dueDate) { toast.error('Informe o vencimento.'); return; }
    setSaving(true);
    try {
      await api.post('/invoices', {
        companyId: form.companyId,
        description: form.description,
        amount,
        dueDate: form.dueDate,
        clientEmail: form.clientEmail || undefined,
        issueNow: form.issueNow,
        sendEmail: form.sendEmail,
      });
      toast.success('Cobrança criada');
      closeModal();
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao criar a cobrança');
    } finally {
      setSaving(false);
    }
  };

  const issue = async (inv: Invoice) => {
    try {
      await api.post(`/invoices/${inv.id}/issue`);
      toast.success('Boleto emitido');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao emitir o boleto');
    }
  };

  const sendMail = async (inv: Invoice) => {
    try {
      const r = await api.post<{ sent: boolean; to: string }>(`/invoices/${inv.id}/send`);
      if (r.sent) toast.success(`E-mail enviado para ${r.to}`);
      else toast.error('Não foi possível enviar o e-mail (verifique o destinatário).');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao enviar o e-mail');
    }
  };

  const pay = async (inv: Invoice) => {
    // Aviso de baixa MANUAL: marca só o controle interno; NÃO baixa/cancela o boleto no
    // Banco Inter (o cliente ainda consegue pagar). Operador deve conciliar manualmente.
    const manualNote = inv.codigoSolicitacao
      ? ' Atenção: esta baixa é apenas de controle interno e NÃO cancela o boleto no Banco Inter (o cliente ainda consegue pagá-lo). Faça a conciliação no banco.'
      : '';
    if (!(await confirm({ title: 'Dar baixa', message: `Confirmar o pagamento da cobrança #${inv.number}?${manualNote}`, confirmLabel: 'Dar baixa' }))) return;
    try {
      await api.post(`/invoices/${inv.id}/pay`, {});
      toast.success('Baixa registrada');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao dar baixa');
    }
  };

  const cancel = async (inv: Invoice) => {
    if (!(await confirm({ title: 'Cancelar cobrança', message: `Cancelar a cobrança #${inv.number}?`, confirmLabel: 'Cancelar cobrança', danger: true }))) return;
    try {
      await api.patch(`/invoices/${inv.id}`, { status: 'canceled' });
      toast.success('Cobrança cancelada');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao cancelar');
    }
  };

  // Abre o PDF do boleto (endpoint autenticado) como blob em nova aba.
  const viewBoleto = async (inv: Invoice) => {
    try {
      const token = await getToken();
      const res = await fetch(`${config.apiUrl}/invoices/${inv.id}/boleto`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error('Não foi possível carregar o boleto.');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao abrir o boleto');
    }
  };

  // Resolve a URL do link para exibição/cópia: o backend pode devolver uma URL ABSOLUTA
  // (quando a base pública está configurada) ou RELATIVA (`/portal/pagar/<token>`). No
  // segundo caso, prefixamos a origem atual para montar um link clicável.
  const resolveLinkUrl = (url: string) => (url.startsWith('/') ? `${window.location.origin}${url}` : url);

  // Mostra o link público de pagamento. Se a cobrança já tem boletoUrl, abre direto; senão
  // gera sob demanda via POST /invoices/:id/payment-link (idempotente) e atualiza a lista.
  const paymentLink = async (inv: Invoice) => {
    try {
      if (inv.boletoUrl) {
        setLinkModal({ open: true, url: resolveLinkUrl(inv.boletoUrl), number: inv.number });
        return;
      }
      const r = await api.post<{ url: string; token: string }>(`/invoices/${inv.id}/payment-link`);
      setLinkModal({ open: true, url: resolveLinkUrl(r.url), number: inv.number });
      toast.success('Link de pagamento gerado');
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar o link de pagamento');
    }
  };

  // Copia o link exibido no modal para a área de transferência.
  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(linkModal.url);
      toast.success('Link copiado');
    } catch {
      toast.error('Não foi possível copiar o link');
    }
  };

  const runGeneration = async () => {
    if (!(await confirm({ title: 'Gerar cobranças do mês', message: 'Gerar as cobranças recorrentes dos contratos para a competência atual?', confirmLabel: 'Gerar' }))) return;
    try {
      const r = await api.post<{ generated: number; issued: number; emailed: number; failed: number }>('/invoices/generate');
      toast.success(`Geradas: ${r.generated} · Emitidas: ${r.issued} · E-mails: ${r.emailed} · Falhas: ${r.failed}`);
      load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao gerar as cobranças');
    }
  };

  // Nome do cliente com alerta de cadastro incompleto (quando aplicável).
  const clientCell = (inv: Invoice) => {
    const missing = inv.companyId ? pendingByCompany.get(inv.companyId) : undefined;
    return (
      <span>
        {inv.clientName || (inv.companyId ? companyName(inv.companyId) : '—')}
        {missing && missing.length > 0 && (
          <span
            className="text-dim"
            title={`Cadastro incompleto para emissão: ${missing.join(', ')}`}
            style={{ marginLeft: 6, cursor: 'help' }}
          >
            ⚠
          </span>
        )}
      </span>
    );
  };

  return (
    <div>
      {/* Cards de resumo */}
      <div className="grid mb-4" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--s4)' }}>
        <Kpi icon="✅" label="Pago" value={brl(summary?.paidAmount)} tone="ok" />
        <Kpi icon="⏰" label="Em atraso" value={brl(summary?.overdueAmount)} tone="bad" />
        <Kpi icon="📅" label="A vencer" value={brl(summary?.dueSoonAmount)} tone="warn" />
        <Kpi icon="📝" label="Rascunhos" value={String(summary?.draftCount ?? 0)} />
      </div>

      {/* Aviso de empresas com cadastro incompleto */}
      {pending.length > 0 && (
        <div className="card mb-3" style={{ borderLeft: '3px solid var(--warning)' }}>
          <strong>⚠ {pending.length} empresa(s) com cadastro incompleto para emissão.</strong>
          <div className="text-dim text-sm mt-1">
            {pending.slice(0, 5).map((p) => `${p.companyName} (${p.missing.join(', ')})`).join(' · ')}
            {pending.length > 5 ? ' …' : ''}
          </div>
        </div>
      )}

      {/* Filtros */}
      <div className="card mb-3">
        <div className="flex gap-2 items-end flex-wrap">
          <div className="field mb-0" style={{ minWidth: 200 }}>
            <label className="label">Empresa</label>
            <select className="select" value={fCompany} onChange={(e) => setFCompany(e.target.value)}>
              <option value="">Todas</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field mb-0" style={{ minWidth: 150 }}>
            <label className="label">Situação</label>
            <select className="select" value={fStatus} onChange={(e) => setFStatus(e.target.value)}>
              <option value="all">Todas</option>
              {Object.entries(InvoiceStatusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </div>
          <div className="field mb-0"><label className="label">Vencimento de</label><input className="input" type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} /></div>
          <div className="field mb-0"><label className="label">até</label><input className="input" type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} /></div>
          <div className="field mb-0" style={{ flex: 1, minWidth: 180 }}>
            <label className="label">Buscar</label>
            <input className="input" value={fSearch} onChange={(e) => setFSearch(e.target.value)} placeholder="cliente, descrição ou nº" />
          </div>
          <button className="btn btn-ghost" onClick={load} disabled={loading}>Aplicar</button>
          <button className="btn btn-ghost" onClick={runGeneration} title="Gerar as cobranças recorrentes do mês">Gerar do mês</button>
          <button className="btn btn-primary" onClick={openNew}>+ Nova cobrança</button>
        </div>
      </div>

      {/* Listagem */}
      <div className="card card-flush">
        {items.length === 0 ? (
          <div className="empty"><div className="empty-icon">💵</div>{loading ? 'Carregando…' : 'Nenhuma cobrança encontrada.'}</div>
        ) : (
          <table className="table"><thead><tr>
            <th>Nº</th><th>Cliente</th><th>Descrição</th><th>Vencimento</th><th>Valor</th><th>Situação</th><th></th>
          </tr></thead><tbody>
            {items.map((inv) => (
              <tr key={inv.id}>
                <td className="font-semibold">#{inv.number}</td>
                <td className="text-sm">{clientCell(inv)}</td>
                <td className="text-muted text-sm">{inv.description}</td>
                <td className="text-muted text-sm">{dateBr(inv.dueDate)}</td>
                <td className="text-sm">{brl(inv.amount)}</td>
                <td><span className={`badge ${statusBadge[inv.status] ?? 'badge-normal'}`}>{InvoiceStatusLabels[inv.status] ?? inv.status}</span></td>
                <td className="text-right" style={{ whiteSpace: 'nowrap' }}>
                  {inv.status === InvoiceStatus.DRAFT && (
                    <button className="btn btn-sm btn-ghost" onClick={() => issue(inv)}>Emitir</button>
                  )}
                  {inv.codigoSolicitacao && (
                    <button className="btn btn-sm btn-ghost" onClick={() => viewBoleto(inv)}>Boleto</button>
                  )}
                  {inv.status !== InvoiceStatus.CANCELED && (
                    <button className="btn btn-sm btn-ghost" onClick={() => paymentLink(inv)}>Link</button>
                  )}
                  {inv.status !== InvoiceStatus.CANCELED && inv.clientEmail && (
                    <button className="btn btn-sm btn-ghost" onClick={() => sendMail(inv)}>E-mail</button>
                  )}
                  {(inv.status === InvoiceStatus.ISSUED || inv.status === InvoiceStatus.OVERDUE) && (
                    <button className="btn btn-sm btn-ghost" onClick={() => pay(inv)}>Baixar</button>
                  )}
                  {inv.status !== InvoiceStatus.PAID && inv.status !== InvoiceStatus.CANCELED && (
                    <button className="btn btn-sm btn-ghost" onClick={() => cancel(inv)}>Cancelar</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody></table>
        )}
      </div>

      {/* Modal: nova cobrança pontual */}
      <Modal
        open={open}
        onClose={closeModal}
        title="Nova cobrança pontual"
        maxWidth={620}
        footer={
          <>
            <button className="btn btn-ghost" type="button" onClick={closeModal}>Cancelar</button>
            <button className="btn btn-primary" type="submit" form="invoice-form" disabled={saving}>
              {saving ? 'Salvando…' : 'Criar cobrança'}
            </button>
          </>
        }
      >
        <form id="invoice-form" onSubmit={save}>
          <div className="field">
            <label className="label">Empresa / cliente</label>
            <select className="select" value={form.companyId} onChange={(e) => onPickCompany(e.target.value)} required>
              <option value="">Selecione…</option>
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            {form.companyId && pendingByCompany.get(form.companyId) && (
              <div className="text-sm" style={{ color: 'var(--warning)', marginTop: 6 }}>
                ⚠ Cadastro incompleto para emissão: {pendingByCompany.get(form.companyId)!.join(', ')}.
              </div>
            )}
          </div>
          <div className="field mt-2"><label className="label">Descrição do serviço</label>
            <input className="input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ex.: Mensalidade de suporte — 10/2026" required />
          </div>
          <div className="field-pair mt-2">
            <div className="field mb-0"><label className="label">Valor (R$)</label>
              <input className="input" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0,00" required />
            </div>
            <div className="field mb-0"><label className="label">Vencimento</label>
              <input className="input" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} required />
              {form.dueDate && form.dueDate < todayIso() && (
                <div className="text-sm" style={{ color: 'var(--warning)', marginTop: 6 }}>
                  Vencimento no passado — confira a data.
                </div>
              )}
            </div>
          </div>
          <div className="field mt-2"><label className="label">E-mail de cobrança</label>
            <input className="input" type="email" value={form.clientEmail} onChange={(e) => setForm({ ...form, clientEmail: e.target.value })} placeholder="cliente@empresa.com" />
          </div>
          <div className="flex gap-3 mt-2 flex-wrap">
            <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={form.issueNow} onChange={(e) => setForm({ ...form, issueNow: e.target.checked })} />
              Emitir boleto agora
            </label>
            <label className="flex items-center gap-2 text-sm" style={{ cursor: 'pointer' }}>
              <input type="checkbox" checked={form.sendEmail} onChange={(e) => setForm({ ...form, sendEmail: e.target.checked })} />
              Enviar e-mail após emitir
            </label>
          </div>
        </form>
      </Modal>

      {/* Modal: link de pagamento público */}
      <Modal
        open={linkModal.open}
        onClose={() => setLinkModal({ open: false, url: '' })}
        title={`Link de pagamento #${linkModal.number ?? ''}`}
        maxWidth={560}
        footer={
          <>
            <button className="btn btn-primary" type="button" onClick={copyLink}>Copiar</button>
            <button className="btn btn-ghost" type="button" onClick={() => setLinkModal({ open: false, url: '' })}>Fechar</button>
          </>
        }
      >
        <p className="text-sm text-muted mb-2">Compartilhe este link público para o cliente pagar a cobrança:</p>
        <input className="input" readOnly value={linkModal.url} onFocus={(e) => e.currentTarget.select()} style={{ fontFamily: 'monospace', wordBreak: 'break-all' }} />
      </Modal>
    </div>
  );
}
